// ────────────────────────────────────────────────────────────────────────────
// Port registry — mirrors the system's real route catalogue
// (My.Directory `routeCatalogue.json`, also exposed to the FE via
// `useRouteCatalogue`). Each ferry port has ONE canonical display name plus the
// real-world variants we see in Directory address data, so the same physical
// port collapses to a single Port chip instead of "Rosslare" vs "Rosslare
// Harbour" vs "Rosslare Europort".
//
// This is the interim source of truth while the bookings recommendation still
// runs off the hardcoded `ferryTimetable`; once that consumes the live
// catalogue (durations / check-in / the routes my placeholder lacks — Cork,
// Larne, Dover→Calais, Bilbao, Zeebrugge, Rotterdam, Dunkirk) the canonical
// names come straight from the catalogue and this map can shrink to the aliases.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Canonical port name → the name variants that all mean that same port. Keyed
 * by the route catalogue's port names; aliases cover the catalogue's own
 * variants (e.g. "Birkenhead (Liverpool)") plus the shorter labels the
 * timetable / address records use.
 */
const PORT_ALIASES: Record<string, readonly string[]> = {
  'Rosslare Europort': ['Rosslare Europort', 'Rosslare', 'Rosslare Harbour', 'Rosslare Port'],
  'Dublin Port': ['Dublin Port', 'Dublin', 'Dublin Ferryport'],
  Holyhead: ['Holyhead', 'Holyhead Port'],
  'Pembroke Dock': ['Pembroke Dock', 'Pembroke'],
  'Fishguard Harbour': ['Fishguard Harbour', 'Fishguard'],
  Liverpool: ['Liverpool', 'Birkenhead', 'Birkenhead (Liverpool)'],
  Belfast: ['Belfast', 'Belfast Port', 'Belfast Harbour'],
  Cairnryan: ['Cairnryan'],
  Larne: ['Larne'],
  Cherbourg: ['Cherbourg'],
  'Ringaskiddy (Cork)': ['Ringaskiddy (Cork)', 'Ringaskiddy', 'Cork'],
  Roscoff: ['Roscoff'],
  Bilbao: ['Bilbao'],
  Zeebrugge: ['Zeebrugge'],
  Rotterdam: ['Rotterdam'],
  Dover: ['Dover'],
  Calais: ['Calais'],
  Dunkirk: ['Dunkirk', 'Dunkerque'],
};

/** Flattened (lowercased alias → canonical) for O(1) exact lookups. */
const ALIAS_TO_CANONICAL = new Map<string, string>(
  Object.entries(PORT_ALIASES).flatMap(([canonical, aliases]) =>
    aliases.map((a) => [a.toLowerCase(), canonical] as const),
  ),
);

/**
 * Map a real-world port label (timetable name, or a Directory address
 * city/name) onto its canonical catalogue port name, so one physical port is
 * one chip. Exact alias match wins; otherwise the canonical port whose alias is
 * contained in — or contains — the raw label (longest alias first, to prefer
 * the most specific). Falls back to the trimmed input when nothing matches (so
 * non-ports like "Ellesmere Port" pass through untouched).
 */
export function canonicalPort(raw: string): string {
  const cleaned = raw.trim();
  if (!cleaned) return cleaned;
  const lower = cleaned.toLowerCase();

  const exact = ALIAS_TO_CANONICAL.get(lower);
  if (exact) return exact;

  let best: { canonical: string; len: number } | null = null;
  for (const [canonical, aliases] of Object.entries(PORT_ALIASES)) {
    for (const alias of aliases) {
      const al = alias.toLowerCase();
      if ((lower.includes(al) || al.includes(lower)) && (!best || al.length > best.len)) {
        best = { canonical, len: al.length };
      }
    }
  }
  return best?.canonical ?? cleaned;
}
